import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApiEu} from '@di-shared/api/di';
import {TripOrderByType} from '@di-shared/api/di/type';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import type {TripHistory} from '@di-shared/api/di/__generated_eu';

export default function useTripDetailEu({
  vin,
  selectedDate,
  orderBy = TripOrderByType.DESC,
}: {
  vin: string;
  selectedDate: Date;
  orderBy?: TripOrderByType;
}) {
  const params = {
    vin,
    date: getYYYYMMddFormat(selectedDate),
    orderBy,
  };

  const {data: tripDetailData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveDailyTripHistory), params],
    queryFn: () => diApiEu.drivingHistory.retrieveDailyTripHistory(params),
    select: ({data}) => data,
  });

  const isEmptyTrip = tripDetailData.content.length === 0;
  const totalDrivingHours = Math.floor(tripDetailData.summary.totalDrvTime / 60);
  const totalDrivingMinutes = tripDetailData.summary.totalDrvTime % 60;

  const formattedTripDetails = tripDetailData.content.map((trip) => ({
    ...trip,
    intervalIgnitionHours: Math.floor(trip.drvTime / 60),
    intervalIgnitionMinutes: trip.drvTime % 60,
    isDrivingLessThanOneMinute: trip.drvTime === 0,
  }));

  const formattedRecentTripDetail: TripHistory & {
    intervalIgnitionHours: number;
    intervalIgnitionMinutes: number;
    isDrivingLessThanOneMinute: boolean;
  } = formattedTripDetails[0];

  return {
    tripDetailData,
    isEmptyTrip,
    totalDrivingHours,
    totalDrivingMinutes,
    formattedTripDetails,
    formattedRecentTripDetail,
  };
}
